/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  useState,
  useCallback,
  type FormEvent,
  type FormEventHandler,
  type FC,
} from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { Heading, Text } from "@vector-im/compound-web";
import { logger } from "matrix-js-sdk/lib/logger";
import { Button } from "@vector-im/compound-web";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import {
  GroupIcon,
  VideoCallIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import {
  createRoom,
  getRelativeRoomUrl,
  roomAliasLocalpartFromRoomName,
  sanitiseRoomNameInput,
} from "../utils/matrix";
import { useGroupCallRooms } from "./useGroupCallRooms";
import { Header, HeaderLogo, LeftNav, RightNav } from "../Header";
import commonStyles from "./common.module.css";
import styles from "./RegisteredView.module.css";
import { FieldRow, InputField, ErrorMessage } from "../input/Input";
import { CallList } from "./CallList";
import { UserMenuContainer } from "../UserMenuContainer";
import { JoinExistingCallModal } from "./JoinExistingCallModal";
import { Form } from "../form/Form";
import { AnalyticsNotice } from "../analytics/AnalyticsNotice";
import { E2eeType } from "../e2ee/e2eeType";
import { useOptInAnalytics, useSetting } from "../settings/settings";
import { useUrlParams } from "../UrlParams";
import { defaultCallType, callQuality } from "../meet/MeetSettings";
import { setPendingCallType } from "../meet/callType";
import CoffeeIcon from "../icons/Coffee.svg?react";
import { HomeNetworkIndicator } from "./HomeNetworkIndicator";
import { Clock } from "../components/Clock";

interface Props {
  client: MatrixClient;
}

export const RegisteredView: FC<Props> = ({ client }) => {
  const { header } = useUrlParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [optInAnalytics] = useOptInAnalytics();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [callType, setCallType] = useSetting(defaultCallType);
  const [maxQuality, setMaxQuality] = useState(false);
  const [joinExistingCallModalOpen, setJoinExistingCallModalOpen] =
    useState(false);
  const onDismissJoinExistingCallModal = useCallback(
    () => setJoinExistingCallModalOpen(false),
    [setJoinExistingCallModalOpen],
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const data = new FormData(e.target as HTMLFormElement);
      const roomNameData = data.get("callName");
      const roomName =
        typeof roomNameData === "string"
          ? sanitiseRoomNameInput(roomNameData)
          : "";

        async function submit(): Promise<void> {
        setError(undefined);
        setLoading(true);

        setPendingCallType(callType);
        // One-tap "Maximum quality": unlock 4K/8K/11K + studio stereo, no caps.
        if (maxQuality) callQuality.setValue("max");

        const createRoomResult = await createRoom(
          client,
          roomName,
          E2eeType.SHARED_KEY,
        );
        if (!createRoomResult.password)
          throw new Error("Failed to create room with shared secret");

        await navigate(
          getRelativeRoomUrl(
            createRoomResult.roomId,
            { kind: E2eeType.SHARED_KEY, secret: createRoomResult.password },
            roomName,
          ),
        );
      }

      submit().catch((error) => {
        if (error.errcode === "M_ROOM_IN_USE") {
          setExistingAlias(roomAliasLocalpartFromRoomName(roomName));
          setLoading(false);
          setError(undefined);
          setJoinExistingCallModalOpen(true);
        } else {
          logger.error(error);
          setLoading(false);
          setError(error);
        }
      });
    },
    [client, navigate, callType, setJoinExistingCallModalOpen],
  );

  const recentRooms = useGroupCallRooms(client);

  const [existingAlias, setExistingAlias] = useState<string>();
  const onJoinExistingRoom = useCallback(() => {
    navigate(`/${existingAlias}`)?.catch((error) => {
      logger.error("Failed to navigate to existing alias", error);
    });
  }, [navigate, existingAlias]);

  return (
    <>
      <div className={commonStyles.container}>
        {header === "standard" && (
          <Header>
            <LeftNav>
              <HeaderLogo />
            </LeftNav>
            <RightNav>
              <UserMenuContainer />
            </RightNav>
          </Header>
        )}
        <main className={commonStyles.main}>
          <HeaderLogo className={commonStyles.logo} />
          <div className={styles.headingRow}>
            <Heading size="lg" weight="semibold">
              {t("start_new_call")}
            </Heading>
            <div className={styles.headerTools}>
              <HomeNetworkIndicator />
              <Clock />
            </div>
          </div>
          <div
            className={styles.callTypeToggle}
            role="group"
            aria-label="Call type"
          >
            <button
              type="button"
              className={classNames(styles.typeBtn, {
                [styles.typeActive]: callType === "meet",
              })}
              aria-pressed={callType === "meet"}
              data-type="meet"
              onClick={() => setCallType("meet")}
            >
              <GroupIcon className={styles.typeIcon} />
              <span>{t("call_type_meet")}</span>
            </button>
            <button
              type="button"
              className={classNames(styles.typeBtn, {
                [styles.typeActive]: callType === "coffee",
              })}
              aria-pressed={callType === "coffee"}
              data-type="coffee"
              onClick={() => setCallType("coffee")}
            >
              <CoffeeIcon className={styles.typeIcon} />
              <span>{t("call_type_coffee")}</span>
            </button>
            <button
              type="button"
              className={classNames(styles.typeBtn, {
                [styles.typeActive]: callType === "video",
              })}
              aria-pressed={callType === "video"}
              data-type="video"
              onClick={() => setCallType("video")}
            >
              <VideoCallIcon className={styles.typeIcon} />
              <span>{t("call_type_video")}</span>
            </button>
          </div>
          <Form className={styles.form} onSubmit={onSubmit}>
            <FieldRow className={styles.fieldRow}>
              <InputField
                id="callName"
                name="callName"
                label={t("call_name")}
                placeholder={t("call_name")}
                type="text"
                required
                autoComplete="off"
                data-testid="home_callName"
              />

              <Button
                type="submit"
                size="lg"
                className={styles.button}
                disabled={loading}
                data-testid="home_go"
              >
                {loading ? t("common.loading") : t("action.go")}
              </Button>
            </FieldRow>
            <FieldRow className={styles.fieldRow}>
              <InputField
                id="maxQuality"
                type="checkbox"
                label={t("home_max_quality")}
                checked={maxQuality}
                onChange={(e) => setMaxQuality(e.target.checked)}
              />
            </FieldRow>
            {optInAnalytics === null && (
              <Text size="sm" className={styles.notice}>
                <AnalyticsNotice />
              </Text>
            )}
            {error && (
              <FieldRow className={styles.fieldRow}>
                <ErrorMessage error={error} />
              </FieldRow>
            )}
          </Form>
          {recentRooms.length > 0 && (
            <CallList rooms={recentRooms} client={client} />
          )}
        </main>
      </div>
      <JoinExistingCallModal
        onJoin={onJoinExistingRoom}
        open={joinExistingCallModalOpen}
        onDismiss={onDismissJoinExistingCallModal}
      />
    </>
  );
};
